import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="bg-white rounded-xl shadow p-8 mb-6">
    <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
    <div className="text-gray-700 leading-relaxed space-y-4 text-[15px]">{children}</div>
  </section>
);

export const PoliticaCorrecoesPage: React.FC = () => {
  const canonicalUrl = `${SITE_URL}/politica-de-correcoes`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `Política de Correções | ${SITE_NAME}`,
    url: canonicalUrl,
    inLanguage: 'pt-BR',
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
  };

  return (
    <>
      <Helmet>
        <title>Política de Correções | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Como o ${SITE_NAME} corrige erros, registra alterações relevantes em artigos e recebe reportes de problemas dos leitores.`}
        />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          <nav className="text-sm text-gray-500 mb-4 flex items-center gap-2">
            <Link to="/" className="hover:text-blue-600 transition">Início</Link>
            <span>›</span>
            <span className="text-gray-700">Política de Correções</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Política de Correções</h1>
          <p className="text-gray-500 mb-8">
            Errar é possível; esconder o erro, não. Esta página explica como tratamos correções no {SITE_NAME}.
            Última atualização: julho de 2026.
          </p>

          <Section title="Como erros são corrigidos">
            <p>
              Ao identificarmos um erro factual — por conta própria, por aviso de leitor ou por atualização da
              fonte original — o artigo é corrigido tão rápido quanto possível. A prioridade é proporcional ao
              potencial de dano: erros que possam influenciar decisões financeiras ou de segurança são tratados
              imediatamente.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Erros factuais relevantes</strong> (números, datas, atribuições, fatos centrais): o texto é corrigido e a correção é registrada no próprio artigo.</li>
              <li><strong>Erros menores</strong> (ortografia, formatação, tradução imprecisa sem mudança de sentido): corrigidos sem nota, refletidos na data de atualização.</li>
              <li><strong>Erros graves que invalidam a notícia</strong>: o artigo é despublicado ou substituído por versão corrigida com aviso explícito.</li>
            </ul>
          </Section>

          <Section title="Como alterações importantes são registradas">
            <p>
              Todo artigo exibe a data de publicação e a data da última atualização. Quando uma correção altera
              o sentido do que foi publicado, adicionamos uma nota de correção ao artigo indicando o que mudou
              e quando. Notícias em desenvolvimento, que recebem novas informações sem que houvesse erro, são
              marcadas como atualizadas — atualização não é o mesmo que correção.
            </p>
          </Section>

          <Section title="Como reportar um problema">
            <p>
              Encontrou um erro, um link quebrado, uma tradução imprecisa ou um conteúdo que viola nossa{' '}
              <Link to="/politica-editorial" className="text-blue-600 hover:underline">Política Editorial</Link>?
              Escreva para{' '}
              <a href="mailto:contact@criptonews.site" className="text-blue-600 hover:underline">
                contact@criptonews.site
              </a>{' '}
              ou use a página de{' '}
              <Link to="/contact" className="text-blue-600 hover:underline">contato</Link>, informando o link
              do artigo e a descrição do problema. Analisamos todos os reportes; correções relevantes são
              aplicadas normalmente em até 2 dias úteis.
            </p>
          </Section>

          <Section title="Como versões atualizadas são identificadas">
            <p>
              A data de última atualização exibida no artigo e enviada nos metadados da página
              (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">dateModified</code> nos dados
              estruturados) sempre reflete a versão corrente. Mecanismos de busca e agregadores recebem essa
              informação via sitemap e dados estruturados.
            </p>
          </Section>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 text-sm text-blue-900">
            Veja também:{' '}
            <Link to="/politica-editorial" className="text-blue-700 font-semibold hover:underline">Política Editorial</Link>
            {' · '}
            <Link to="/sobre" className="text-blue-700 font-semibold hover:underline">Sobre o {SITE_NAME}</Link>
          </div>
        </div>
      </div>
    </>
  );
};
